<template>
  <section class="sync-center">
    <div class="page-heading">
      <div>
        <h2>钉钉同步中心</h2>
        <p>集中查看组织与人员同步进度、结果及失败明细。</p>
      </div>
      <el-button icon="el-icon-refresh" :loading="loading" @click="query">刷新</el-button>
    </div>

    <el-card shadow="never" class="filter-card">
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item label="学校">
          <el-select v-model="filters.schoolDepartmentId" clearable filterable placeholder="全部学校">
            <el-option v-for="item in schoolOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="通道">
          <el-select v-model="filters.channel" clearable placeholder="全部通道">
            <el-option label="钉钉" value="DingDing" />
          </el-select>
        </el-form-item>
        <el-form-item label="通讯录">
          <el-select v-model="filters.bigType" clearable placeholder="全部类型">
            <el-option label="内部通讯录" :value="0" />
            <el-option label="家校通讯录" :value="1" />
          </el-select>
        </el-form-item>
        <el-form-item label="资源">
          <el-select v-model="filters.resourceType" clearable placeholder="全部资源">
            <el-option label="组织" value="Department" />
            <el-option label="学生" value="Student" />
            <el-option label="教师" value="Teacher" />
            <el-option label="全部" value="All" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="filters.status" clearable placeholder="全部状态">
            <el-option label="待执行" :value="0" />
            <el-option label="执行中" :value="1" />
            <el-option label="成功" :value="2" />
            <el-option label="部分失败" :value="3" />
            <el-option label="失败" :value="4" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button @click="resetFilters">重置</el-button>
          <el-button
            type="danger"
            plain
            icon="el-icon-delete"
            :disabled="!selectedTaskRows.length"
            :loading="batchDeletingTasks"
            @click="batchDeleteTasks"
          >批量删除</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card shadow="never" class="task-card">
      <el-table
        ref="taskTable"
        v-loading="loading"
        :data="rows"
        border
        stripe
        :row-class-name="taskRowClassName"
        @selection-change="onTaskSelectionChange"
        @row-click="handleTaskRowClick"
      >
        <el-table-column type="selection" width="42" :selectable="canDeleteTask" />
        <el-table-column label="同步范围" min-width="200" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ taskScopeText(row) }}</template>
        </el-table-column>
        <el-table-column label="通道" width="88">
          <template slot-scope="{ row }">{{ valueOf(row, 'Channel') || 'DingDing' }}</template>
        </el-table-column>
        <el-table-column label="通讯录" width="110">
          <template slot-scope="{ row }">{{ Number(valueOf(row, 'BigType')) === 0 ? '内部通讯录' : '家校通讯录' }}</template>
        </el-table-column>
        <el-table-column label="资源" width="100">
          <template slot-scope="{ row }">{{ resourceText(valueOf(row, 'ResourceType')) }}</template>
        </el-table-column>
        <el-table-column label="进度" min-width="190">
          <template slot-scope="{ row }">
            <el-progress :percentage="progressOf(row)" :status="progressStatus(row)" />
          </template>
        </el-table-column>
        <el-table-column label="成功/失败/跳过" width="160" align="center">
          <template slot-scope="{ row }">
            <span class="success-count">{{ numberOf(row, 'SuccessCount') }}</span>
            / <span class="failed-count">{{ numberOf(row, 'FailCount') }}</span>
            / <span class="skip-count">{{ numberOf(row, 'SkipCount') }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="statusTag(valueOf(row, 'Status'))">{{ statusText(valueOf(row, 'Status')) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="CreateTime" label="提交时间" width="170" />
        <el-table-column prop="FinishTime" label="完成时间" width="170" />
        <el-table-column label="操作" width="200" fixed="right" align="center">
          <template slot-scope="{ row }">
            <div class="table-op-pair">
              <el-button type="text" @click.stop="openDetail(row)">明细</el-button>
              <el-button v-if="canRetry(row)" type="text" class="retry-action" :loading="retryingId === taskId(row)"
                @click.stop="retryTask(row)">重试</el-button>
              <el-button
                v-if="canDeleteTask(row)"
                type="text"
                class="delete-action"
                :loading="taskDeletingId === taskId(row)"
                @click.stop="deleteTask(row)"
              >删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination class="pagination" background layout="total, sizes, prev, pager, next, jumper"
        :current-page="page" :page-size="pageSize" :page-sizes="[10, 20, 50]" :total="total"
        @current-change="handlePageChange" @size-change="handleSizeChange" />
    </el-card>

    <el-dialog  title="同步任务明细" :visible.sync="detailVisible" width="900px" append-to-body>
      <div v-if="currentTask" class="task-summary">
        <span><em>资源</em>{{ resourceText(currentTask.ResourceType) }}</span>
        <span><em>状态</em>{{ statusText(currentTask.Status) }}</span>
        <span><em>范围</em>{{ taskScopeText(currentTask) }}</span>
        <span><em>开始时间</em>{{ currentTask.StartTime || '-' }}</span>
        <span><em>完成时间</em>{{ currentTask.FinishTime || '-' }}</span>
      </div>
      <div class="detail-filter">
        <el-radio-group v-model="detailResultFilter" size="small" @change="handleDetailFilterChange">
          <el-radio-button :label="0">全部</el-radio-button>
          <el-radio-button :label="1">成功</el-radio-button>
          <el-radio-button :label="2">失败</el-radio-button>
        </el-radio-group>
        <div class="detail-count-filter" title="留空不过滤；填 0 即只看数量大于 0 的明细">
          <span>数量 &gt;</span>
          <el-input-number
            v-model="detailMinItemCount"
            size="small"
            :min="0"
            :max="999999"
            :precision="0"
            :controls="false"
            placeholder="N"
            @change="handleDetailFilterChange"
            @keyup.enter.native="handleDetailFilterChange"
          />
        </div>
        <el-button
          type="danger"
          plain
          size="small"
          icon="el-icon-delete"
          :disabled="!selectedDetailRows.length || !canDeleteCurrentTask"
          :loading="batchDeletingDetails"
          @click="batchDeleteDetails"
        >批量删除</el-button>
      </div>
      <el-table
        ref="detailTable"
        v-loading="detailLoading"
        :data="detailRows"
        border
        max-height="520"
        :row-class-name="detailRowClassName"
        @selection-change="onDetailSelectionChange"
        @row-click="handleDetailRowClick"
      >
        <el-table-column type="selection" width="42" :selectable="() => canDeleteCurrentTask" />
        <el-table-column label="资源名称" min-width="170" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ resourceText(valueOf(row, 'ResourceName')) }}</template>
        </el-table-column>
        <el-table-column label="资源标识" min-width="130" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ resourceKeyText(row) }}</template>
        </el-table-column>
        <el-table-column label="结果" width="90">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="valueOf(row, 'IsSuccess') ? 'success' : 'danger'">
              {{ valueOf(row, 'IsSuccess') ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="110">
          <template slot-scope="{ row }">{{ operationText(valueOf(row, 'Operation')) }}</template>
        </el-table-column>
        <el-table-column prop="ItemCount" label="数量" width="80" />
        <el-table-column prop="ResultMessage" label="处理信息" min-width="250" show-overflow-tooltip />
        <el-table-column prop="CreateTime" label="执行时间" width="170" />
        <el-table-column label="操作" width="90" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              v-if="canDeleteCurrentTask"
              type="text"
              class="delete-action"
              :loading="detailDeletingId === detailId(row)"
              @click.stop="deleteDetail(row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination class="pagination" background layout="total, sizes, prev, pager, next"
        :current-page="detailPage" :page-size="detailPageSize" :page-sizes="[10, 20, 50]" :total="detailTotal"
        @current-change="handleDetailPageChange" @size-change="handleDetailSizeChange" />
    </el-dialog>
  </section>
</template>

<script>
import {
  getSchoolDepartmentTree,
  getDingTalkSyncTaskPage,
  getDingTalkSyncTask,
  getDingTalkSyncTaskDetailPage,
  retryDingTalkSyncTask,
  deleteDingTalkSyncTask,
  batchDeleteDingTalkSyncTasks,
  deleteDingTalkSyncTaskDetail,
  batchDeleteDingTalkSyncTaskDetails,
  getTeachingDepartmentTree,
  getInternalDepartmentTree,
  getDepartmentNamesMap,
} from '../../api/modules/department'
import {
  flattenDepartmentTree,
  formatTaskScopeText,
  collectMissingScopeDepartmentIds,
} from '../../utils/dingTalkSyncScope'

export default {
  name: 'DingTalkSyncTaskCenter',
  data() {
    return {
      loading: false,
      rows: [],
      page: 1,
      pageSize: 10,
      total: 0,
      filters: { schoolDepartmentId: '', channel: '', bigType: '', resourceType: '', status: '' },
      schoolOptions: [],
      detailVisible: false,
      detailLoading: false,
      detailRows: [],
      detailPage: 1,
      detailPageSize: 10,
      detailTotal: 0,
      currentTaskId: '',
      currentTask: null,
      detailResultFilter: 0,
      detailMinItemCount: null,
      retryingId: '',
      selectedTaskRows: [],
      selectedDetailRows: [],
      taskDeletingId: '',
      detailDeletingId: '',
      batchDeletingTasks: false,
      batchDeletingDetails: false,
      scopeDeptFlatCache: {},
      scopeDeptNameById: {},
    }
  },
  computed: {
    canDeleteCurrentTask() {
      return this.currentTask && this.canDeleteTask(this.currentTask)
    },
  },
  mounted() {
    this.loadSchools()
    this.query()
  },
  methods: {
    /** 同时兼容后端大小写字段 */
    valueOf(row, key) {
      if (!row) return ''
      const lowerKey = key.charAt(0).toLowerCase() + key.slice(1)
      return row[key] !== undefined ? row[key] : row[lowerKey]
    },
    numberOf(row, key) {
      return Number(this.valueOf(row, key) || 0)
    },
    taskId(row) {
      return this.valueOf(row, 'Id') || this.valueOf(row, 'TaskId')
    },
    detailId(row) {
      return this.valueOf(row, 'Id') || this.valueOf(row, 'DetailId')
    },
    canDeleteTask(row) {
      return [0, 1].indexOf(Number(this.valueOf(row, 'Status'))) === -1
    },
    onTaskSelectionChange(rows) {
      this.selectedTaskRows = rows || []
    },
    /** 点击行切换任务勾选。最后更新：2026-08-30 */
    handleTaskRowClick(row, column, event) {
      if (!row || !this.canDeleteTask(row)) return
      if (event && (event.target.closest('.el-button') || event.target.closest('.el-checkbox'))) return
      const table = this.$refs.taskTable
      if (!table) return
      table.toggleRowSelection(row)
    },
    taskRowClassName({ row }) {
      return this.canDeleteTask(row) ? 'is-row-selectable' : 'is-row-disabled'
    },
    onDetailSelectionChange(rows) {
      this.selectedDetailRows = rows || []
    },
    /** 点击行切换明细勾选。最后更新：2026-08-30 */
    handleDetailRowClick(row, column, event) {
      if (!row || !this.canDeleteCurrentTask) return
      if (event && (event.target.closest('.el-button') || event.target.closest('.el-checkbox'))) return
      const table = this.$refs.detailTable
      if (!table) return
      table.toggleRowSelection(row)
    },
    detailRowClassName() {
      return this.canDeleteCurrentTask ? 'is-row-selectable' : 'is-row-disabled'
    },
    async loadSchools() {
      const res = await getSchoolDepartmentTree({ pid: 0 })
      const root = res.data && res.data.response
      const result = []
      const walk = node => {
        if (!node) return
        const type = String(node.DeptOrgType || node.deptOrgType || '').toLowerCase()
        if (type === 'school' || type === 'affiliateschool') result.push({ value: node.value, label: node.label })
        ;(node.children || []).forEach(walk)
      }
      walk(root)
      this.schoolOptions = result
    },
    async query() {
      this.loading = true
      try {
        const query = {
          PageIndex: this.page,
          PageSize: this.pageSize,
          SchoolDepartmentId: this.filters.schoolDepartmentId || null,
          Channel: this.filters.channel || null,
          BigType: this.filters.bigType === '' ? null : this.filters.bigType,
          ResourceType: this.filters.resourceType || null,
          Status: this.filters.status === '' ? null : this.filters.status,
        }
        const res = await getDingTalkSyncTaskPage(query)
        const response = (res.data && res.data.response) || {}
        this.rows = response.data || response.Data || []
        this.total = Number(response.dataCount || response.DataCount || 0)
        await this.prefetchScopeIndexesForRows(this.rows)
        await this.resolveTaskScopeNames(this.rows)
      } finally {
        this.loading = false
      }
    },
    scopeDeptCacheKey(schoolId, bigType) {
      return `${schoolId}-${bigType}`
    },
    async ensureScopeDeptIndex(schoolId, bigType) {
      const normalizedSchoolId = Number(schoolId)
      if (!normalizedSchoolId) return []
      const cacheKey = this.scopeDeptCacheKey(normalizedSchoolId, bigType)
      if (this.scopeDeptFlatCache[cacheKey]) return this.scopeDeptFlatCache[cacheKey]
      try {
        const loader = Number(bigType) === 0 ? getInternalDepartmentTree : getTeachingDepartmentTree
        const res = await loader({ pid: 0, scopeDepartmentId: normalizedSchoolId })
        const flat = flattenDepartmentTree(res && res.data && res.data.response)
        this.$set(this.scopeDeptFlatCache, cacheKey, flat)
        return flat
      } catch (e) {
        this.$set(this.scopeDeptFlatCache, cacheKey, [])
        return []
      }
    },
    async prefetchScopeIndexesForRows(rows) {
      const tasks = []
      const seen = new Set()
      ;(rows || []).forEach((row) => {
        const schoolId = Number(this.valueOf(row, 'SchoolDepartmentId'))
        const bigType = Number(this.valueOf(row, 'BigType'))
        if (!schoolId) return
        const cacheKey = this.scopeDeptCacheKey(schoolId, bigType)
        if (seen.has(cacheKey)) return
        seen.add(cacheKey)
        tasks.push(this.ensureScopeDeptIndex(schoolId, bigType))
      })
      if (tasks.length) await Promise.all(tasks)
    },
    taskScopeText(row) {
      const schoolId = Number(this.valueOf(row, 'SchoolDepartmentId'))
      const bigType = Number(this.valueOf(row, 'BigType'))
      const cacheKey = this.scopeDeptCacheKey(schoolId, bigType)
      const deptFlatByBigType = { [bigType]: this.scopeDeptFlatCache[cacheKey] || [] }
      return formatTaskScopeText(row, deptFlatByBigType, this.scopeDeptNameById)
    },
    async resolveTaskScopeNames(rows) {
      const deptFlatByBigType = { 0: [], 1: [] }
      ;(rows || []).forEach((row) => {
        const schoolId = Number(this.valueOf(row, 'SchoolDepartmentId'))
        const bigType = Number(this.valueOf(row, 'BigType'))
        const cacheKey = this.scopeDeptCacheKey(schoolId, bigType)
        deptFlatByBigType[bigType] = this.scopeDeptFlatCache[cacheKey] || []
      })
      const missingIds = collectMissingScopeDepartmentIds(rows, deptFlatByBigType, this.scopeDeptNameById)
      if (!missingIds.length) return
      const nameMap = await getDepartmentNamesMap(missingIds)
      this.scopeDeptNameById = Object.assign({}, this.scopeDeptNameById, nameMap)
    },
    handleSearch() {
      this.page = 1
      this.query()
    },
    resetFilters() {
      this.filters = { schoolDepartmentId: '', channel: '', bigType: '', resourceType: '', status: '' }
      this.handleSearch()
    },
    handlePageChange(page) {
      this.page = page
      this.query()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.page = 1
      this.query()
    },
    progressOf(row) {
      const rawProgress = this.valueOf(row, 'Progress')
      const explicit = Number(rawProgress)
      if (rawProgress !== '' && rawProgress !== null && Number.isFinite(explicit)) {
        return Math.max(0, Math.min(100, explicit))
      }
      const total = this.numberOf(row, 'TotalCount')
      const done = this.numberOf(row, 'SuccessCount') + this.numberOf(row, 'FailCount') + this.numberOf(row, 'SkipCount')
      return total > 0 ? Math.min(100, Math.round(done * 100 / total)) : 0
    },
    progressStatus(row) {
      const status = Number(this.valueOf(row, 'Status'))
      if (status === 4) return 'exception'
      if (status === 2) return 'success'
      return undefined
    },
    resourceText(value) {
      return ({ department: '组织', student: '学生', teacher: '教师', all: '全部' })[String(value).toLowerCase()] || value || '-'
    },
    operationText(value) {
      return ({
        sync: '同步',
        skipped: '跳过',
        initialize: '初始化',
        retry: '重试',
      })[String(value || '').toLowerCase()] || value || '-'
    },
    resourceKeyText(row) {
      const key = this.valueOf(row, 'ResourceKey')
      const mapped = this.resourceText(key)
      return mapped === key ? (key || '-') : mapped
    },
    statusText(value) {
      return ({ 0: '待执行', 1: '执行中', 2: '成功', 3: '部分失败', 4: '失败' })[Number(value)] || '-'
    },
    statusTag(value) {
      return ({ 0: 'info', 1: 'warning', 2: 'success', 3: 'warning', 4: 'danger' })[Number(value)] || 'info'
    },
    canRetry(row) {
      return [3, 4].indexOf(Number(this.valueOf(row, 'Status'))) !== -1
    },
    async openDetail(row) {
      this.currentTaskId = this.taskId(row)
      this.detailPage = 1
      this.detailResultFilter = 0
      this.detailMinItemCount = null
      this.selectedDetailRows = []
      this.detailVisible = true
      const taskRes = await getDingTalkSyncTask(this.currentTaskId)
      this.currentTask = taskRes.data && taskRes.data.response
      await this.loadDetail()
    },
    async loadDetail() {
      this.detailLoading = true
      try {
        const res = await getDingTalkSyncTaskDetailPage({
          TaskId: this.currentTaskId,
          ResultFilter: this.detailResultFilter,
          MinItemCount: this.detailMinItemCount === null || this.detailMinItemCount === ''
            ? null
            : Number(this.detailMinItemCount),
          PageIndex: this.detailPage,
          PageSize: this.detailPageSize,
        })
        const response = (res.data && res.data.response) || {}
        this.detailRows = response.data || response.Data || []
        this.detailTotal = Number(response.dataCount || response.DataCount || 0)
      } finally {
        this.detailLoading = false
      }
    },
    handleDetailPageChange(page) {
      this.detailPage = page
      this.loadDetail()
    },
    handleDetailSizeChange(size) {
      this.detailPageSize = size
      this.detailPage = 1
      this.loadDetail()
    },
    handleDetailFilterChange() {
      this.detailPage = 1
      this.loadDetail()
    },
    async retryTask(row) {
      const taskId = this.taskId(row)
      await this.$confirm('仅重试该任务中的失败项，确认继续吗？', '失败重试', { type: 'warning' })
      this.retryingId = taskId
      try {
        const res = await retryDingTalkSyncTask({ TaskId: taskId })
        if (res.data && res.data.success) {
          this.$message.success(res.data.msg || '重试任务已提交')
          this.query()
        } else {
          this.$message.error((res.data && res.data.msg) || '重试失败')
        }
      } finally {
        this.retryingId = ''
      }
    },
    async deleteTask(row) {
      if (!this.canDeleteTask(row)) {
        this.$message.warning('待执行或执行中的任务不可删除')
        return
      }
      const id = this.taskId(row)
      await this.$confirm(`确认删除任务 ${id} 及其全部明细吗？`, '删除任务', { type: 'warning' })
      this.taskDeletingId = id
      try {
        const res = await deleteDingTalkSyncTask({ TaskId: id })
        if (res.data && res.data.success) {
          this.$message.success(res.data.msg || '删除成功')
          if (String(this.currentTaskId) === String(id)) {
            this.detailVisible = false
            this.currentTask = null
            this.currentTaskId = ''
          }
          this.query()
        } else {
          this.$message.error((res.data && res.data.msg) || '删除失败')
        }
      } finally {
        this.taskDeletingId = ''
      }
    },
    async batchDeleteTasks() {
      if (!this.selectedTaskRows.length) return
      const ids = this.selectedTaskRows.map((row) => this.taskId(row)).filter(Boolean)
      if (!ids.length) return
      await this.$confirm(`确认删除选中的 ${ids.length} 条任务及其全部明细吗？`, '批量删除', { type: 'warning' })
      this.batchDeletingTasks = true
      try {
        const res = await batchDeleteDingTalkSyncTasks({ TaskIds: ids })
        if (res.data && res.data.success) {
          this.$message.success(res.data.msg || '删除成功')
          if (ids.some((id) => String(id) === String(this.currentTaskId))) {
            this.detailVisible = false
            this.currentTask = null
            this.currentTaskId = ''
          }
          this.selectedTaskRows = []
          this.query()
        } else {
          this.$message.error((res.data && res.data.msg) || '删除失败')
        }
      } finally {
        this.batchDeletingTasks = false
      }
    },
    async deleteDetail(row) {
      if (!this.canDeleteCurrentTask) {
        this.$message.warning('任务执行中，暂不可删除明细')
        return
      }
      const id = this.detailId(row)
      await this.$confirm('确认删除该条明细吗？', '删除明细', { type: 'warning' })
      this.detailDeletingId = id
      try {
        const res = await deleteDingTalkSyncTaskDetail({ DetailId: id })
        if (res.data && res.data.success) {
          this.$message.success(res.data.msg || '删除成功')
          const taskRes = await getDingTalkSyncTask(this.currentTaskId)
          this.currentTask = taskRes.data && taskRes.data.response
          await this.loadDetail()
          this.query()
        } else {
          this.$message.error((res.data && res.data.msg) || '删除失败')
        }
      } finally {
        this.detailDeletingId = ''
      }
    },
    async batchDeleteDetails() {
      if (!this.selectedDetailRows.length || !this.canDeleteCurrentTask) return
      const ids = this.selectedDetailRows.map((row) => this.detailId(row)).filter(Boolean)
      if (!ids.length) return
      await this.$confirm(`确认删除选中的 ${ids.length} 条明细吗？`, '批量删除', { type: 'warning' })
      this.batchDeletingDetails = true
      try {
        const res = await batchDeleteDingTalkSyncTaskDetails({
          TaskId: this.currentTaskId,
          DetailIds: ids,
        })
        if (res.data && res.data.success) {
          this.$message.success(res.data.msg || '删除成功')
          const taskRes = await getDingTalkSyncTask(this.currentTaskId)
          this.currentTask = taskRes.data && taskRes.data.response
          this.selectedDetailRows = []
          await this.loadDetail()
          this.query()
        } else {
          this.$message.error((res.data && res.data.msg) || '删除失败')
        }
      } finally {
        this.batchDeletingDetails = false
      }
    },
  },
}
</script>

<style scoped>
.sync-center { min-height: 100%; padding: 18px; box-sizing: border-box; background: #f5f7fa; }
.page-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.page-heading h2 { margin: 0 0 6px; color: #303133; font-size: 22px; }
.page-heading p { margin: 0; color: #909399; font-size: 13px; }
.filter-card, .task-card { border-radius: 8px; border-color: #e4e7ed; }
.task-card { margin-top: 14px; }
.filter-card /deep/ .el-card__body { padding-bottom: 4px; }
.pagination { margin-top: 16px; text-align: right; }
.success-count { color: #67c23a; font-weight: 600; }
.failed-count, .retry-action { color: #f56c6c; font-weight: 600; }
.delete-action,
.delete-action:focus,
.delete-action:hover {
  color: #f56c6c !important;
}
.skip-count { color: #909399; }
.task-summary { display: grid; grid-template-columns: repeat(3, 1fr); margin-bottom: 12px; border: 1px solid #ebeef5; }
.task-summary span { padding: 9px 12px; border-right: 1px solid #ebeef5; border-bottom: 1px solid #ebeef5; color: #606266; }
.task-summary em { margin-right: 8px; color: #909399; font-style: normal; }
.detail-filter { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.detail-count-filter { display: flex; align-items: center; gap: 8px; color: #606266; font-size: 13px; }
.detail-count-filter .el-input-number { width: 96px; }
.task-card >>> .el-table__body tr.is-row-disabled { cursor: not-allowed; }
.task-card >>> .el-table__body tr.is-row-disabled:hover > td { background: inherit !important; }
</style>
